import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

const cell = { padding: '8px 10px', borderBottom: '1px solid #e2e8f0', fontSize: 13, verticalAlign: 'top' };
const statCard = { background: '#f8fafc', borderRadius: 12, padding: '14px 16px', minWidth: 170 };

export default function AdminPlacesPage() {
    const { t } = useTranslation(['adminMaps', 'common']);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgIsError, setMsgIsError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminApi().get('/admin/places/stats');
            setStats(res.data);
        } catch {
            setMsg(t('adminMaps:places.loadError'));
            setMsgIsError(true);
        }
        setLoading(false);
    }, [t]);

    useEffect(() => { load(); }, [load]);

    async function rebuild() {
        setBusy(true);
        setMsg('');
        try {
            const res = await adminApi().post('/admin/places/rebuild');
            setStats(res.data.stats);
            setMsg(t('adminMaps:places.rebuilt', { count: res.data.processed }));
            setMsgIsError(false);
        } catch {
            setMsg(t('adminMaps:places.rebuildError'));
            setMsgIsError(true);
        }
        setBusy(false);
    }

    const stat = (label, value) => (
        <div style={statCard}>
            <div style={{ fontSize: 24, fontWeight: 800 }}>{value}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
        </div>
    );

    return (
        <OwnerPageLayout title={t('adminMaps:places.pageTitle')} maxWidth={1000}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{t('adminMaps:places.subtitle')}</p>

            {msg && (
                <p style={{
                    marginBottom: 12, fontSize: 13, padding: '10px 14px', borderRadius: 8,
                    background: msgIsError ? '#fdecea' : '#e8f5e9', color: msgIsError ? '#c0392b' : '#2e7d32',
                }}>
                    {msg}
                </p>
            )}

            <div className="sp-panel" style={{ marginBottom: 16 }}>
                <button type="button" className="sp-btn sp-btn--primary" disabled={busy} onClick={rebuild}>
                    {busy ? t('adminMaps:places.rebuilding') : t('adminMaps:places.rebuild')}
                </button>
                <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0' }}>{t('adminMaps:places.rebuildHint')}</p>
            </div>

            {loading ? (
                <div className="sp-loading-page">{t('common:loading')}</div>
            ) : stats && (
                <>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                        {stat(t('adminMaps:places.statExact'), stats.exactPlaces)}
                        {stat(t('adminMaps:places.statApprox'), stats.approximatePlaces)}
                        {stat(t('adminMaps:places.statSchoolsExact'), stats.schoolsOnMapExact)}
                        {stat(t('adminMaps:places.statSchoolsApprox'), stats.schoolsOnMapApproximate)}
                        {stat(t('adminMaps:places.statNone'), stats.schoolsWithoutLocation)}
                    </div>

                    <div className="sp-panel" style={{ marginBottom: 16 }}>
                        <h3 style={{ margin: '0 0 10px' }}>{t('adminMaps:places.top')}</h3>
                        {stats.topPlaces.length === 0 ? (
                            <p style={{ fontSize: 13, color: '#64748b' }}>{t('adminMaps:places.empty')}</p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                    {stats.topPlaces.map(p => (
                                        <tr key={p.id}>
                                            <td style={cell}>{p.addressFull || p.city}</td>
                                            <td style={{ ...cell, whiteSpace: 'nowrap', textAlign: 'right' }}>
                                                {p.schools} {t('adminMaps:places.topSchools')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="sp-panel">
                        <h3 style={{ margin: '0 0 4px' }}>{t('adminMaps:places.unlocated')}</h3>
                        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px' }}>{t('adminMaps:places.unlocatedHint')}</p>
                        {stats.unlocated.length === 0 ? (
                            <p style={{ fontSize: 13, color: '#64748b' }}>{t('adminMaps:places.empty')}</p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', fontSize: 12, color: '#64748b' }}>
                                        <th style={cell}>{t('adminMaps:places.colSchool')}</th>
                                        <th style={cell}>{t('adminMaps:places.colCity')}</th>
                                        <th style={cell}>{t('adminMaps:places.colCountry')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.unlocated.map(s => (
                                        <tr key={s.id}>
                                            <td style={{ ...cell, fontWeight: 600 }}>{s.name}</td>
                                            <td style={cell}>{s.city}</td>
                                            <td style={cell}>{s.country}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}
        </OwnerPageLayout>
    );
}
